import type { Chart, ChartType, Plugin } from 'chart.js'
import { setCursor } from '../utils/cursor'

const PLUGIN_ID = 'indicateClickOnPointHover'

type IndicateClickOnPointHoverOpts = {
  disabled?: boolean
  radiusOffset?: number
}

type HoveredPoint = {
  datasetIndex: number
  index: number
} | null

const hoveredByChart = new WeakMap<Chart, HoveredPoint>()

export const indicateClickOnPointHover: Plugin<
  ChartType,
  IndicateClickOnPointHoverOpts
> = {
  id: PLUGIN_ID,

  afterDatasetsDraw(chart, _args, opts) {
    if (opts?.disabled) return

    const hovered = hoveredByChart.get(chart)

    if (!hovered) return

    const meta = chart.getDatasetMeta(hovered.datasetIndex)
    const el: any = meta?.data?.[hovered.index]

    if (!el || isNaN(el.x) || isNaN(el.y)) return

    const baseRadius = el.options?.radius ?? 0
    const offset = opts?.radiusOffset ?? 2
    const r = baseRadius + offset
    const color =
      typeof meta.dataset?.options?.borderColor === 'string'
        ? meta.dataset.options.borderColor
        : '#000'

    const { ctx } = chart

    ctx.save()

    // White border for contrast
    ctx.beginPath()
    ctx.arc(el.x, el.y, r + 1, 0, Math.PI * 2)
    ctx.fillStyle = '#fff'
    ctx.fill()

    // Colored fill
    ctx.beginPath()
    ctx.arc(el.x, el.y, r, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()

    ctx.restore()
  },

  afterEvent(chart, args, opts) {
    if (opts?.disabled) return

    const ev = args.event

    if (ev.type === 'mouseout') {
      const prev = hoveredByChart.get(chart)

      hoveredByChart.delete(chart)
      setCursor(chart, PLUGIN_ID, null)

      if (prev) chart.draw()

      return
    }

    if (ev.type !== 'mousemove') return
    if (ev.x == null || ev.y == null) return

    const native = ev.native as Event | null

    if (!native) return

    const hits = chart.getElementsAtEventForMode(
      native,
      'nearest',
      { intersect: false },
      true
    )

    const prev = hoveredByChart.get(chart)
    let next: HoveredPoint = null

    if (hits.length) {
      const { datasetIndex, index } = hits[0]
      const isMain = (chart.data.datasets[datasetIndex] as any).main

      if (isMain) {
        const meta = chart.getDatasetMeta(datasetIndex)
        const el: any = meta?.data?.[index]

        if (el) {
          const dist = Math.sqrt((ev.x - el.x) ** 2 + (ev.y - el.y) ** 2)
          const hitRadius = el.options?.hitRadius ?? 8

          if (dist <= hitRadius) {
            next = { datasetIndex, index }
          }
        }
      }
    }

    const changed =
      prev?.datasetIndex !== next?.datasetIndex || prev?.index !== next?.index

    if (changed) {
      hoveredByChart.set(chart, next)
      setCursor(chart, PLUGIN_ID, next ? 'pointer' : null)
      chart.draw()
    }
  }
}
